import type {
  AuditBase,
  Block,
  ConfidenceLevel,
  UserFlowPriority as Prio,
  Test,
  TestContext,
} from "../types";

// --- Assumed shared types (adjust if you already have these elsewhere) ---
export type Ref = string; // refine if you have a structured Ref
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyDict = Record<string, any>;

// --- Base + intent models ---
export interface BaseIntent {
  reasoning: string;
  tasks_pending: boolean;
  confidence: ConfidenceLevel;
}

export interface DoneIntent extends BaseIntent {
  status: "success" | "failure";
  message: string;
}

export interface NextTaskIntent extends BaseIntent {
  next_task: string;
}

export interface UserInputIntent extends BaseIntent {
  question: string;
  priority: Prio;
}

export interface UserInputResponse {
  response: string;
}

export interface ClarificationIntent extends BaseIntent {
  refs: Ref[]; // default []
  highlighted_refs: Ref[]; // default []
  type: "screenshot" | "expand_text";
}

export interface ActionIntent extends BaseIntent {
  refs: Ref[]; // default []
  type:
    | "click"
    | "click_download"
    | "type"
    | "fill"
    | "wait"
    | "press"
    | "goto"
    | "scroll"
    | "hover"
    | "select"
    | "check"
    | "uncheck";
  args?: number | string | AnyDict | null;
}

// --- General timing/status wrapper used by Intent & HistoryItem ---
export type GeneralStatus = "pending" | "in_progress" | "completed" | "failed";

export interface General {
  started_at?: string; // ISO datetime
  start_url?: string;
  ended_at?: string; // ISO datetime
  end_url?: string;
  elapsed_ms?: number;
  status: GeneralStatus; // default "pending"
  error?: string;
}

// --- Generic wrappers using DoneIntent constraint ---
export interface Intent<TDone extends DoneIntent = DoneIntent> extends General {
  intent: ActionIntent | ClarificationIntent | NextTaskIntent | UserInputIntent | TDone;
}

export const isDoneIntent = <TDone extends DoneIntent = DoneIntent>(
  intent: Intent<TDone>["intent"],
): intent is TDone => {
  return Object.hasOwn(intent, "status");
};

export const isUserInputIntent = <TDone extends DoneIntent = DoneIntent>(
  intent: Intent<TDone>["intent"],
): intent is UserInputIntent => {
  return Object.hasOwn(intent, "question");
};

export const isNextTaskIntent = <TDone extends DoneIntent = DoneIntent>(
  intent: Intent<TDone>["intent"],
): intent is NextTaskIntent => {
  return Object.hasOwn(intent, "next_task");
};

export const isClarificationIntent = <TDone extends DoneIntent = DoneIntent>(
  intent: Intent<TDone>["intent"],
): intent is ClarificationIntent => {
  return Object.hasOwn(intent, "type") && (intent as AnyDict).type !== undefined;
};

export const isActionIntent = <TDone extends DoneIntent = DoneIntent>(
  intent: Intent<TDone>["intent"],
): intent is ActionIntent => {
  return Object.hasOwn(intent, "type") && (intent as AnyDict).type !== undefined;
};

// ExpandedRef
export interface ExpandedRef {
  ref: string;
  description?: string;
  text: string;
}

export interface HistoryItemMessage {
  message: string;
  timestamp: string; // ISO datetime
}

// History item
export interface HistoryItem<TDone extends DoneIntent = DoneIntent> extends General {
  id: string;
  task: string;
  task_added_at?: string; // ISO datetime
  intents: Intent<TDone>[]; // default []
  messages?: HistoryItemMessage[]; // default []
  block_ids: number[]; // default []
  expanded_refs: ExpandedRef[]; // default []
}

// CustomDoneIntent
export interface CustomDoneIntent extends DoneIntent {
  issues: string[]; // default []
  human_question?: string;
  explanation: string;
}

// --- Incoming events to the agent (from client) ---
export type TestAgentV2IncomingEventType =
  | "next_task"
  | "response"
  | "close"
  | "pause"
  | "run"
  | "set_model_name"
  | "set_persona_id"
  | "set_app_config_id"
  | "set_test_dependency"
  | "toggle_autopilot"
  | "change_name"
  | "save_test";

export interface TestAgentV2IncomingEvent {
  type: TestAgentV2IncomingEventType;
  message?: string;
  id?: string;
}

// --- Streamed/status events from the agent ---
export type TestAgentV2Status =
  | "pending"
  | "idle"
  | "running"
  | "paused"
  | "pending"
  | "need_user_input"
  | "closed";

export interface TestAgentV2HistoryEvent<TDone extends DoneIntent = DoneIntent> {
  history: HistoryItem<TDone>;
}

export interface TestAgentV2BlockEvent {
  block: Block;
}

export interface TestAgentV2StatusEvent {
  status: TestAgentV2Status;
}

export interface TestAgentV2FullHistoryEvent<TDone extends DoneIntent = DoneIntent> {
  history: HistoryItem<TDone>[];
}

export interface TestAgentV2ConfigEvent {
  name: string;
  agent_id: string;
  model_name: string;
  liveview_url: string;
  page_url?: string | null;
  autopilot: boolean;
  app_config_id?: string | null;
  persona_id?: string | null;
  organization_id?: string | null;
  group_key?: string | null;
  test_dependency_ids: string[]; // default []
}

export interface TestAgentV2UserInputEvent {
  question: string;
  priority: Prio;
}

export interface TestAgentV2TestEvent {
  test: Test;
  ctxs: TestContext[];
}

export interface TestAgentV2NotifyEvent {
  message: string;
  level: "info" | "warning" | "error";
}

export type TestAgentV2EventType =
  | "config"
  | "history"
  | "block"
  | "status"
  | "full_history"
  | "user_input"
  | "test"
  | "notify";

export type TestAgentV2EventData<TDone extends DoneIntent = DoneIntent> =
  | TestAgentV2ConfigEvent
  | TestAgentV2HistoryEvent<TDone>
  | TestAgentV2BlockEvent
  | TestAgentV2StatusEvent
  | TestAgentV2FullHistoryEvent<TDone>
  | TestAgentV2UserInputEvent
  | TestAgentV2TestEvent
  | TestAgentV2NotifyEvent;

export interface TestAgentV2Event<TDone extends DoneIntent = DoneIntent> {
  ts: string; // ISO datetime
  type: TestAgentV2EventType;
  data: TestAgentV2EventData<TDone>;
}

export interface TestAgentV2Data {
  agent_id: string;
  name?: string;

  liveview_url: string;
  recording_path?: string | null;
  page_url?: string | null;

  autopilot: boolean;
  model_name: string;
  done_intent?: string | null;

  organization_id?: string | null;
  group_key?: string | null;

  app_config_id?: string | null;
  persona_id?: string | null;

  test_id: string;
  dependency_test_ids: string[];
  blocks: Block[];

  history: HistoryItem<DoneIntent>[];

  last_done?: DoneIntent | null;
  pending_user_input?: UserInputIntent | null;

  status: TestAgentV2Status;
}

export interface TestAgentV2Session extends AuditBase {
  id: string;
  data: TestAgentV2Data;
}

// Done Intent Types
export type TestAgentDoneIntentType =
  | "next_qa_task"
  | "test_creator"
  | "extraction"
  | "app_analysis"
  | "discovery_prompt";

// Application Analysis Types
export type DiscoveredElementType = "page" | "form" | "button" | "link" | "interaction";

export interface DiscoveredElement {
  element_type: DiscoveredElementType;
  name: string;
  url?: string | null;
  description: string;
}

export interface ApplicationAnalysisDoneIntent extends DoneIntent {
  discovered_elements: DiscoveredElement[];
  coverage_analysis: string;
}

// Discovery Prompt Types
export interface DiscoveryPromptReference {
  entity_id: string;
  entity_type: string;
  summary: string;
}

export interface DiscoveryPromptDoneIntent extends DoneIntent {
  test_dependency_id?: string | null;
  test_count: number;
  persona_id?: string | null;
  intent: string;
  intent_rationale: string;
  app_config_id: string;
  reference_ids: DiscoveryPromptReference[];
  variant: "discovery" | "edge_case";
}

// Action Intent Type (extracted from ActionIntent)
export type ActionIntentType =
  | "click"
  | "click_download"
  | "type"
  | "fill"
  | "wait"
  | "press"
  | "goto"
  | "scroll"
  | "hover"
  | "select"
  | "check"
  | "uncheck";

// Available Done Intents
export type AvailableDoneIntents =
  | "qa"
  | "test_creator"
  | "extract"
  | "app_analysis"
  | "app_knowledge"
  | "discovery_prompt";

// Set Variable Action (for test execution)
export interface SetVariableAction {
  key: string;
  value: string;
  type?: "sensitive" | "url" | "string" | "number" | "boolean";
  context?: string;
}

// Test Agent V2 Params (configuration for agent initialization)
export interface TestAgentV2Params {
  // Running
  task: string;
  max_iterations?: number; // default 100

  // Config
  source?: string | null;
  agent_id?: string | null;
  model_name?: string; // default "google/gemini-2.5-flash"
  autopilot?: boolean; // default false

  app_config_id?: string | null;
  variables?: SetVariableAction[] | null;

  persona_id?: string | null;
  organization_id?: string | null;
  group_key?: string | null;
  context_url?: string | null;

  store_video?: boolean; // default true
  use_vision?: boolean; // default false

  test_dependency?: string | null;
  append_system_prompt?: string | null;
  continue_sentence?: string; // default DEFAULT_CONTINUE_SENTENCE

  done_intent?: AvailableDoneIntents | null;

  enable_user_input?: boolean; // default true
  enable_extraction?: boolean; // default true
  enable_filesystem?: boolean; // default false
  enable_rag?: boolean; // default false

  allowed_actions?: ActionIntentType[] | null;
  restricted_actions?: ActionIntentType[] | null;

  store_axe_issues?: boolean; // default false
  store_graph?: boolean; // default false

  animate_cursor?: boolean; // default true
  enable_element_highlighting?: boolean; // default true

  use_storage_path?: boolean; // default false

  // Locator optimization
  enable_locator_optimization?: boolean; // default false
  locator_optimization_threshold?: number; // default 7
  use_ai_locator_validation?: boolean; // default true

  ws_endpoint?: string | null;
  cdp_endpoint?: string | null;

  // End
  persist_test?: boolean; // default false
  test_name?: string | null;

  wait_for_statuses?: TestAgentV2Status[] | null;

  meta?: AnyDict; // default {}
}
